import type { IntlShape } from "react-intl";
import { toFakeUtcISO } from "@framework/provider-asu";

import {
  IAnomalyChronogramData,
  IAnomalyChronogramItem,
  IAnomalyChronogramNode,
  IAnomalyMeasureTrendData,
  IAnomalyMeasureTrendMetadata,
  IAnomalyMeasureTrendPoint,
  IChronogramTick,
  IRulPredictItem,
  ISelectedForecastTrend,
  ITciTrendRequest,
  IVisibleChronogramRow,
  TForecastTrendObjectType,
} from "./interfaces";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TICKS_COUNT = 6;

export const CHRONOGRAM_NAME_CELL_WIDTH = 280;
export const DEFAULT_FORECAST_EXTENSION_DAYS = 3;
export const MAX_FORECAST_EXTENSION_DAYS = 90;
export const FORECAST_EXTENSION_BOUNDARY_COLOR = "#7e22ce";
export const FORECAST_CHART_PLOT_PADDING = {
  top: 28,
  right: 0,
  bottom: 44,
  left: 0,
} as const;

export const FORECAST_TIMELINE_GRID_SX = {
  display: "grid",
  gridTemplateColumns: `${CHRONOGRAM_NAME_CELL_WIDTH}px minmax(0, 1fr)`,
  width: "100%",
} as const;

export const FORECAST_TIMELINE_PLOT_CELL_SX = {
  px: 1.5,
  minWidth: 0,
} as const;

export const getForecastTimeDomain = (
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): { minTime: number; maxTime: number } | null => {
  const parsedStart = new Date(startTimestamp).getTime();
  const parsedEnd = new Date(endTimestamp).getTime();

  if (!Number.isFinite(parsedStart) || !Number.isFinite(parsedEnd)) {
    return null;
  }

  return {
    minTime: parsedStart,
    maxTime: parsedEnd > parsedStart ? parsedEnd : parsedStart + 1,
  };
};

export const buildForecastWeekStartTimestamp = (endTimestamp: string | Date): string | null => {
  const parsedEnd = new Date(endTimestamp).getTime();

  return Number.isFinite(parsedEnd) ? new Date(parsedEnd - 7 * DAY_IN_MS).toISOString() : null;
};

export const resolveForecastTrendRefresh = ({
  previousEndTimestamp,
  nextEndTimestamp,
  isRecorderMode,
  hasPendingExplicitRangeChange,
  hasManualTrends,
}: {
  previousEndTimestamp: string | Date;
  nextEndTimestamp: string | Date;
  isRecorderMode: boolean;
  hasPendingExplicitRangeChange: boolean;
  hasManualTrends: boolean;
}): { clearManualTrends: boolean; consumePendingRangeChange: boolean; shouldLoad: boolean } => {
  const previousEnd = new Date(previousEndTimestamp).getTime();
  const nextEnd = new Date(nextEndTimestamp).getTime();
  const hasEndChanged = Number.isFinite(previousEnd) && Number.isFinite(nextEnd) && previousEnd !== nextEnd;

  if (hasPendingExplicitRangeChange && !hasEndChanged) {
    return {
      clearManualTrends: false,
      consumePendingRangeChange: false,
      shouldLoad: false,
    };
  }

  const applyPendingRangeChange = hasPendingExplicitRangeChange && hasEndChanged;
  const clearManualTrends = hasManualTrends && (applyPendingRangeChange || (hasEndChanged && !isRecorderMode));

  return {
    clearManualTrends,
    consumePendingRangeChange: applyPendingRangeChange,
    shouldLoad: !clearManualTrends,
  };
};

export const normalizeForecastExtensionDays = (value: number): number =>
  Number.isFinite(value)
    ? Math.min(MAX_FORECAST_EXTENSION_DAYS, Math.max(1, Math.trunc(value)))
    : DEFAULT_FORECAST_EXTENSION_DAYS;

export const buildForecastTimelineEndTimestamp = (
  endTimestamp: string | Date,
  extensionDays: number,
): string | null => {
  const parsedEnd = new Date(endTimestamp).getTime();

  if (!Number.isFinite(parsedEnd)) {
    return null;
  }

  const normalizedExtensionDays = Number.isFinite(extensionDays)
    ? Math.min(MAX_FORECAST_EXTENSION_DAYS, Math.max(0, Math.trunc(extensionDays)))
    : 0;
  const timelineEnd = new Date(parsedEnd + normalizedExtensionDays * DAY_IN_MS);

  return Number.isFinite(timelineEnd.getTime()) ? timelineEnd.toISOString() : null;
};

const ANOMALY_CHRONOGRAM_MID_COLOR = { red: 255, green: 255, blue: 0 };
const ANOMALY_CHRONOGRAM_MAX_COLOR = { red: 255, green: 0, blue: 0 };
const ANOMALY_CHRONOGRAM_MIN_COLOR = { red: 23, green: 137, blue: 0 };

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const normalizeItem = (item: unknown, dbTz: number): IAnomalyChronogramItem | null => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const dateValue = typeof (item as { Date?: unknown }).Date === "string" ? (item as { Date: string }).Date : null;
  const Value = toNumber((item as { Value?: unknown }).Value);

  if (!dateValue || Value === null || !Number.isFinite(new Date(dateValue).getTime())) {
    return null;
  }

  return { Date: toFakeUtcISO(dateValue, dbTz), Value };
};

const normalizeTrendPoint = (item: unknown, dbTz: number): IAnomalyMeasureTrendPoint | null => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const dateValue = typeof (item as { Date?: unknown }).Date === "string" ? (item as { Date: string }).Date : null;
  const Value = toNumber((item as { Value?: unknown }).Value);

  if (!dateValue || Value === null || !Number.isFinite(new Date(dateValue).getTime())) {
    return null;
  }

  return { Date: toFakeUtcISO(dateValue, dbTz), Value };
};

const normalizeNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return toNumber(value);
};

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized || null;
};

const normalizeTrendMetadata = (metadata: unknown): IAnomalyMeasureTrendMetadata | null => {
  if (!metadata || typeof metadata !== "object") {
    return null;
  }

  const idTrain = toNumber((metadata as { idTrain?: unknown }).idTrain);
  const trainName = normalizeText((metadata as { trainName?: unknown }).trainName);
  const idPoint = toNumber((metadata as { idPoint?: unknown }).idPoint);
  const pointName = normalizeText((metadata as { pointName?: unknown }).pointName);
  const idMeasure = toNumber((metadata as { idMeasure?: unknown }).idMeasure);
  const measureName = normalizeText((metadata as { measureName?: unknown }).measureName);

  if (idTrain === null || !trainName || idPoint === null || !pointName || idMeasure === null || !measureName) {
    return null;
  }

  return {
    idTrain,
    trainName,
    idTrainMechanism: normalizeNullableNumber((metadata as { idTrainMechanism?: unknown }).idTrainMechanism),
    mechanismName: normalizeText((metadata as { mechanismName?: unknown }).mechanismName),
    idPoint,
    pointName,
    idMeasure,
    measureName,
    units: normalizeNullableNumber((metadata as { units?: unknown }).units),
    alarmLevel2: normalizeNullableNumber((metadata as { alarmLevel2?: unknown }).alarmLevel2),
    alarmLevel3: normalizeNullableNumber((metadata as { alarmLevel3?: unknown }).alarmLevel3),
    alarmLevel4: normalizeNullableNumber((metadata as { alarmLevel4?: unknown }).alarmLevel4),
    alarmType: normalizeNullableNumber((metadata as { alarmType?: unknown }).alarmType),
  };
};

const normalizeNode = (node: unknown, dbTz: number): IAnomalyChronogramNode | null => {
  if (!node || typeof node !== "object") {
    return null;
  }

  const id = toNumber((node as { id?: unknown }).id);
  const name = typeof (node as { name?: unknown }).name === "string" ? (node as { name: string }).name.trim() : "";
  const objectType =
    typeof (node as { objectType?: unknown }).objectType === "string"
      ? (node as { objectType: string }).objectType
      : null;

  if (
    id === null ||
    !name ||
    (objectType !== "train" && objectType !== "mechanism" && objectType !== "point" && objectType !== "measure")
  ) {
    return null;
  }

  const items = Array.isArray((node as { items?: unknown[] }).items)
    ? (node as { items: unknown[] }).items
        .map(item => normalizeItem(item, dbTz))
        .filter((item): item is IAnomalyChronogramItem => item !== null)
    : [];

  const children = Array.isArray((node as { children?: unknown[] }).children)
    ? (node as { children: unknown[] }).children
        .map(child => normalizeNode(child, dbTz))
        .filter((child): child is IAnomalyChronogramNode => child !== null)
    : [];

  return {
    objectType,
    id,
    name,
    items: items.sort((left, right) => new Date(left.Date).getTime() - new Date(right.Date).getTime()),
    children,
  };
};

export const normalizeAnomalyChronogramData = (
  json: IAnomalyChronogramData | null | undefined,
  dbTz = 0,
): IAnomalyChronogramData => ({
  root: normalizeNode(json?.root ?? null, dbTz),
});

export const createEmptyAnomalyMeasureTrendData = (): IAnomalyMeasureTrendData => ({
  metadata: null,
  history: [],
  forecast: [],
  anomalies: [],
});

const normalizeTrendPoints = (items: unknown, dbTz: number): IAnomalyMeasureTrendPoint[] =>
  Array.isArray(items)
    ? items
        .map(item => normalizeTrendPoint(item, dbTz))
        .filter((item): item is IAnomalyMeasureTrendPoint => item !== null)
        .sort((left, right) => new Date(left.Date).getTime() - new Date(right.Date).getTime())
    : [];

export const normalizeAnomalyMeasureTrendData = (
  json: IAnomalyMeasureTrendData | null | undefined,
  dbTz = 0,
): IAnomalyMeasureTrendData => ({
  metadata: normalizeTrendMetadata(json?.metadata ?? null),
  history: normalizeTrendPoints(json?.history, dbTz),
  forecast: normalizeTrendPoints(json?.forecast, dbTz),
  anomalies: normalizeTrendPoints(json?.anomalies, dbTz),
});

export const normalizeTciTrendData = (
  json: { history: unknown; forecast: unknown } | null | undefined,
  dbTz = 0,
): IAnomalyMeasureTrendData => ({
  metadata: null,
  history: normalizeTrendPoints(json?.history, dbTz),
  forecast: normalizeTrendPoints(json?.forecast, dbTz),
  anomalies: [],
});

export const hasTrendLineData = (data: IAnomalyMeasureTrendData | undefined): boolean =>
  Boolean(data && (data.history.length > 0 || data.forecast.length > 0));

export const getForecastTrendKey = (objectType: TForecastTrendObjectType, id: number): string => `${objectType}:${id}`;

export const getChronogramNodeKey = (node: IAnomalyChronogramNode): string => `${node.objectType}:${node.id}`;

export const createSelectedForecastTrend = (node: IAnomalyChronogramNode): ISelectedForecastTrend | null => {
  if (node.objectType === "point") {
    return null;
  }

  return {
    key: getForecastTrendKey(node.objectType, node.id),
    objectType: node.objectType,
    id: node.id,
    name: node.name,
  };
};

export const createDefaultTrainTciTrend = (idTrain: number, trainName: string): ISelectedForecastTrend => ({
  key: getForecastTrendKey("train", idTrain),
  objectType: "train",
  id: idTrain,
  name: trainName,
});

export const applyPinnedTrainTciTrend = (
  selectedTrends: ISelectedForecastTrend[],
  pinnedTrend: ISelectedForecastTrend | null,
): ISelectedForecastTrend[] =>
  pinnedTrend ? [pinnedTrend, ...selectedTrends.filter(trend => trend.key !== pinnedTrend.key)] : selectedTrends;

export const toggleSelectedForecastTrend = (
  selectedTrends: ISelectedForecastTrend[],
  node: IAnomalyChronogramNode,
): ISelectedForecastTrend[] => {
  const trend = createSelectedForecastTrend(node);

  if (!trend) {
    return selectedTrends;
  }

  return selectedTrends.some(selected => selected.key === trend.key)
    ? selectedTrends.filter(selected => selected.key !== trend.key)
    : [...selectedTrends, trend];
};

export const isTciForecastTrend = (trend: ISelectedForecastTrend): boolean =>
  trend.objectType === "train" || trend.objectType === "mechanism";

export const groupForecastTrends = (
  trends: ISelectedForecastTrend[],
  combineTciTrends: boolean,
  combineAllTrends: boolean,
  isTrainTciTrendPinned: boolean,
): ISelectedForecastTrend[][] => {
  if (trends.length === 0) {
    return [];
  }

  if (combineAllTrends) {
    if (!isTrainTciTrendPinned) {
      return [trends];
    }

    const tciTrends = trends.filter(isTciForecastTrend);
    const measureTrends = trends.filter(trend => !isTciForecastTrend(trend));

    return [tciTrends, measureTrends].filter(group => group.length > 0);
  }

  if (!combineTciTrends) {
    return trends.map(trend => [trend]);
  }

  const tciTrends = trends.filter(isTciForecastTrend);
  const measureTrends = trends.filter(trend => !isTciForecastTrend(trend));

  return [tciTrends, ...measureTrends.map(trend => [trend])].filter(group => group.length > 0);
};

export const buildTciTrendRequest = (
  trend: ISelectedForecastTrend,
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): ITciTrendRequest | null => {
  if (trend.objectType === "train") {
    return {
      url: `/predictive/train-tci-trend/${trend.id}`,
      data: {
        startTimestamp,
        endTimestamp,
      },
    };
  }

  if (trend.objectType === "mechanism") {
    return {
      url: `/predictive/mechanism-tci-trend/${trend.id}`,
      data: {
        startTimestamp,
        endTimestamp,
      },
    };
  }

  return null;
};

export const getRenderableForecastTrends = (
  selectedTrends: ISelectedForecastTrend[],
  trendDataByKey: Record<string, IAnomalyMeasureTrendData>,
  pinnedTrendKey?: string | null,
): ISelectedForecastTrend[] =>
  selectedTrends.filter(
    trend => trend.key === pinnedTrendKey || !isTciForecastTrend(trend) || hasTrendLineData(trendDataByKey[trend.key]),
  );

export const flattenVisibleChronogramRows = (
  root: IAnomalyChronogramNode | null,
  expandedKeys: string[],
): IVisibleChronogramRow[] => {
  if (!root) {
    return [];
  }

  const rows: IVisibleChronogramRow[] = [];

  const appendNode = (node: IAnomalyChronogramNode, depth: number): void => {
    const key = getChronogramNodeKey(node);
    rows.push({ depth, key, node });

    if (!expandedKeys.includes(key)) {
      return;
    }

    node.children.forEach(child => appendNode(child, depth + 1));
  };

  appendNode(root, 0);

  return rows;
};

const padChronogramTickPart = (value: number): string => String(value).padStart(2, "0");

const formatChronogramTick = (date: Date, duration: number): string => {
  const year = date.getUTCFullYear();
  const month = padChronogramTickPart(date.getUTCMonth() + 1);
  const day = padChronogramTickPart(date.getUTCDate());
  const hours = padChronogramTickPart(date.getUTCHours());
  const minutes = padChronogramTickPart(date.getUTCMinutes());

  if (duration <= DAY_IN_MS) {
    return `${hours}:${minutes}`;
  }

  if (duration <= 7 * DAY_IN_MS) {
    return `${day}.${month} ${hours}:${minutes}`;
  }

  if (duration <= 90 * DAY_IN_MS) {
    return `${day}.${month}`;
  }

  if (duration <= 366 * DAY_IN_MS) {
    return `${month}.${year}`;
  }

  return String(year);
};

export const buildChronogramTicks = (startTimestamp: string | Date, endTimestamp: string | Date): IChronogramTick[] => {
  const domain = getForecastTimeDomain(startTimestamp, endTimestamp);

  if (!domain) {
    return [];
  }

  const { minTime: start, maxTime: end } = domain;
  const duration = end - start;

  return Array.from({ length: TICKS_COUNT + 1 }, (_, index) => {
    const ratio = index / TICKS_COUNT;
    const date = new Date(start + duration * ratio);

    return {
      key: `chronogram-tick-${index}`,
      left: ratio * 100,
      label: formatChronogramTick(date, duration),
    };
  });
};

export const getAnomalyWeightColor = (value: number): string => {
  const normalized = clamp(value, 0, 1);

  if (normalized <= 0.5) {
    const ratio = normalized / 0.5;
    const red = Math.round(
      ANOMALY_CHRONOGRAM_MIN_COLOR.red + (ANOMALY_CHRONOGRAM_MID_COLOR.red - ANOMALY_CHRONOGRAM_MIN_COLOR.red) * ratio,
    );
    const green = Math.round(
      ANOMALY_CHRONOGRAM_MIN_COLOR.green +
        (ANOMALY_CHRONOGRAM_MID_COLOR.green - ANOMALY_CHRONOGRAM_MIN_COLOR.green) * ratio,
    );
    const blue = Math.round(
      ANOMALY_CHRONOGRAM_MIN_COLOR.blue +
        (ANOMALY_CHRONOGRAM_MID_COLOR.blue - ANOMALY_CHRONOGRAM_MIN_COLOR.blue) * ratio,
    );

    return `rgb(${red}, ${green}, ${blue})`;
  }

  const ratio = (normalized - 0.5) / 0.5;
  const red = Math.round(
    ANOMALY_CHRONOGRAM_MID_COLOR.red + (ANOMALY_CHRONOGRAM_MAX_COLOR.red - ANOMALY_CHRONOGRAM_MID_COLOR.red) * ratio,
  );
  const green = Math.round(
    ANOMALY_CHRONOGRAM_MID_COLOR.green +
      (ANOMALY_CHRONOGRAM_MAX_COLOR.green - ANOMALY_CHRONOGRAM_MID_COLOR.green) * ratio,
  );
  const blue = Math.round(
    ANOMALY_CHRONOGRAM_MID_COLOR.blue + (ANOMALY_CHRONOGRAM_MAX_COLOR.blue - ANOMALY_CHRONOGRAM_MID_COLOR.blue) * ratio,
  );

  return `rgb(${red}, ${green}, ${blue})`;
};

export const getForecastTimestampOffset = (
  timestamp: string | Date,
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): number | null => {
  const domain = getForecastTimeDomain(startTimestamp, endTimestamp);
  const parsedDate = new Date(timestamp).getTime();

  if (!domain || !Number.isFinite(parsedDate)) {
    return null;
  }

  const clamped = clamp(parsedDate, domain.minTime, domain.maxTime);

  return ((clamped - domain.minTime) / (domain.maxTime - domain.minTime)) * 100;
};

export const getChronogramItemOffset = (
  item: IAnomalyChronogramItem,
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): number | null => {
  const domain = getForecastTimeDomain(startTimestamp, endTimestamp);
  const timestamp = new Date(item.Date).getTime();

  if (!domain || !Number.isFinite(timestamp) || timestamp < domain.minTime || timestamp > domain.maxTime) {
    return null;
  }

  return ((timestamp - domain.minTime) / (domain.maxTime - domain.minTime)) * 100;
};

export const formatRulPredictPercent = (value: number, formatNumber: IntlShape["formatNumber"]): string =>
  formatNumber(value, {
    style: "percent",
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });

const getRulTiming = (
  item: Pick<IRulPredictItem, "date" | "rulDays">,
  intervalEndTimestamp: string | Date,
): Pick<IRulTimelineModel, "elapsedDays" | "remainingDays"> | null => {
  if (item.rulDays === null || !Number.isFinite(item.rulDays)) {
    return null;
  }

  const predictionTimestamp = new Date(item.date).getTime();
  const intervalEnd = new Date(intervalEndTimestamp).getTime();

  if (!Number.isFinite(predictionTimestamp) || !Number.isFinite(intervalEnd)) {
    return null;
  }

  const elapsedDays = Math.max(0, (intervalEnd - predictionTimestamp) / DAY_IN_MS);

  return {
    elapsedDays,
    remainingDays: Math.max(0, item.rulDays - elapsedDays),
  };
};

export interface IRulTimelineMarker {
  horizonDays: 7 | 30 | 60 | 90;
  daysFromIntervalEnd: number;
  riskKey: keyof IRulPredictItem["risk"];
  value: number;
}

export interface IRulTimelineModel {
  elapsedDays: number;
  remainingDays: number;
  markers: IRulTimelineMarker[];
}

export const getRulRemainingDays = (
  item: Pick<IRulPredictItem, "date" | "rulDays">,
  intervalEndTimestamp: string | Date,
): number | null => getRulTiming(item, intervalEndTimestamp)?.remainingDays ?? null;

const RUL_RISK_HORIZONS = [
  { horizonDays: 7, riskKey: "risk7d" },
  { horizonDays: 30, riskKey: "risk30d" },
  { horizonDays: 60, riskKey: "risk60d" },
  { horizonDays: 90, riskKey: "risk90d" },
] as const;

export const buildRulTimeline = (
  item: IRulPredictItem,
  intervalEndTimestamp: string | Date,
): IRulTimelineModel | null => {
  const timing = getRulTiming(item, intervalEndTimestamp);

  if (!timing) {
    return null;
  }

  const { elapsedDays, remainingDays } = timing;
  const markers = RUL_RISK_HORIZONS.flatMap(({ horizonDays, riskKey }) => {
    const value = item.risk[riskKey];
    const daysFromIntervalEnd = horizonDays - elapsedDays;

    return value !== null && daysFromIntervalEnd >= 0 && daysFromIntervalEnd <= remainingDays
      ? [{ horizonDays, riskKey, value, daysFromIntervalEnd }]
      : [];
  });

  return { elapsedDays, remainingDays, markers };
};

export const hasRulPredictData = (item: IRulPredictItem): boolean =>
  item.rulDays !== null || item.alarmStatus !== null || Object.values(item.risk).some(value => value !== null);

const isRulRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeRulRisk = (risk: unknown): IRulPredictItem["risk"] => ({
  risk7d: normalizeNullableNumber(isRulRecord(risk) ? risk.risk7d : null),
  risk30d: normalizeNullableNumber(isRulRecord(risk) ? risk.risk30d : null),
  risk60d: normalizeNullableNumber(isRulRecord(risk) ? risk.risk60d : null),
  risk90d: normalizeNullableNumber(isRulRecord(risk) ? risk.risk90d : null),
});

export const normalizeRulPredictItem = (item: unknown): IRulPredictItem | null => {
  if (!isRulRecord(item)) {
    return null;
  }

  const idTrainMechanism = toNumber(item.idTrainMechanism);
  const mechanismName = normalizeText(item.mechanismName) ?? "";
  const date = normalizeText(item.date) ?? "";

  if (idTrainMechanism === null) {
    return null;
  }

  return {
    idTrainMechanism,
    mechanismName,
    date,
    rulDays: normalizeNullableNumber(item.rulDays),
    risk: normalizeRulRisk(item.risk),
    alarmStatus: normalizeNullableNumber(item.alarmStatus),
  };
};

export const normalizeRulPredictItems = (items: unknown): IRulPredictItem[] =>
  Array.isArray(items)
    ? items.map(normalizeRulPredictItem).filter((item): item is IRulPredictItem => item !== null)
    : [];
